import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import type { StudioProgress } from '@/lib/api'
import { KaijuLoader } from '../KaijuLoader'

interface Props {
  sessionId: string
  onComplete: () => void
  onError: (message: string) => void
}

const STAGE_LABELS: Record<string, string> = {
  'Analyzing structure': 'Scanning document structure...',
  'Analysis complete': 'Structure mapped',
  'Enhancing content': 'Improving clarity and formatting...',
  'Content enhanced': 'Content polished',
  'Generating visualizations': 'Suggesting images and charts...',
  'Visualizations generated': 'Image suggestions ready',
  'Suggestions ready': 'Review your image suggestions',
  'Assembling document': 'Building final document...',
  'Complete': 'Document complete!',
}

export function StudioProgressOverlay({ sessionId, onComplete, onError }: Props) {
  const [progress, setProgress] = useState<StudioProgress>({ stage: 'Starting...', percent: 5, message: 'Initializing pipeline...' })

  useEffect(() => {
    let active = true
    const poll = async () => {
      try {
        const result = await api.studio.status(sessionId)
        if (!active) return

        setProgress(result.progress)

        // Pipeline now pauses at suggestions_ready (or completes at completed)
        if (result.status === 'completed' || result.status === 'suggestions_ready') {
          onComplete()
          return
        }
        if (result.status === 'failed') {
          onError(result.progress.message || 'Pipeline failed')
          return
        }

        // Keep polling
        setTimeout(poll, 2000)
      } catch {
        if (active) setTimeout(poll, 3000)
      }
    }

    poll()
    return () => { active = false }
  }, [sessionId, onComplete, onError])

  const percent = progress.percent ?? 0
  const stageLabel = STAGE_LABELS[progress.stage || ''] || progress.stage || 'Processing...'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-white/10 shadow-2xl p-8 max-w-md w-full mx-4">
        <div className="flex flex-col items-center text-center">
          <KaijuLoader size="md" />

          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mt-4">
            Beautifying Document
          </h3>

          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {stageLabel}
          </p>

          {progress.message && progress.message !== stageLabel && (
            <p className="text-xs text-gray-400 mt-1">
              {progress.message}
            </p>
          )}

          {/* Progress bar */}
          <div className="w-full mt-6">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>{progress.stage || 'Starting'}</span>
              <span>{percent}%</span>
            </div>
            <div className="w-full h-2 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>

          {/* Stage indicators */}
          <div className="flex items-center gap-2 mt-4">
            {['Analyze', 'Enhance', 'Suggest'].map((stage, i) => {
              const stagePercents = [25, 55, 80] as const
              const threshold = stagePercents[i] ?? 100
              const prevThreshold = i > 0 ? (stagePercents[i - 1] ?? 0) : 0
              const isDone = percent >= threshold
              const isActive = percent < threshold && (i === 0 || percent >= prevThreshold)
              return (
                <div
                  key={stage}
                  className={`px-2 py-1 text-xs rounded-full transition-colors ${
                    isDone
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : isActive
                      ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 animate-pulse'
                      : 'bg-gray-100 text-gray-400 dark:bg-white/5 dark:text-gray-500'
                  }`}
                >
                  {stage}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
